import React, { useContext, RefObject, useRef, useEffect } from 'react'
import { arrayOf, string, shape, oneOfType, bool, node } from 'prop-types'
import { colorForClass, ThemeContext, defaultTheme } from '@ied/ui-core'
import { SelectBase as RMWCSelect, SelectProps } from '@rmwc/select'

import './styles.scss'

interface UISelectProps extends SelectProps {
  menuRef: RefObject<HTMLElement>
}

class UISelect extends RMWCSelect {
  constructor(props: UISelectProps) {
    super(props)
  }

  componentDidMount() {
    super.componentDidMount()

    if (this.menu) {
      const anchor = (this.props as UISelectProps).menuRef.current
      if (anchor) {
        this.menu.setAnchorElement(anchor)
        this.menu.menuSurface?.unhoistMenuFromBody()
      }
    }
  }
}

export interface CompProps extends UISelectProps {
  color?: string
  className?: string
  style?: any
  native: boolean
  enhanced: true
  fullwidth: boolean
}

const Select: React.FC<CompProps> = (props: CompProps) => {
  const {
    color,
    className,
    style,
    native,
    enhanced,
    fullwidth,
    placeholder,
    ...rest
  } = props

  const containerRef = useRef(null as any)

  useEffect(() => {
    const { current } = containerRef
    if (!current) return

    const width = current.clientWidth ? `${current.clientWidth}px` : 'auto'
    current.style.setProperty('--menu-width', width)
  }, [containerRef])

  const theme = useContext(ThemeContext) || defaultTheme
  const { primary, secondary, typography } = theme
  let finalColor = secondary.main
  if (color === 'primary') {
    finalColor = primary.main
  } else if (color === 'secondary') {
    finalColor = secondary.main
  } else if (color) {
    finalColor = colorForClass(color).hexValue()
  }

  const selectStyle = {
    ...style,
    '--mdc-theme-primary': finalColor,
    '--primary-main': finalColor,
    '--font-family': typography?.fontFamily,
  }

  let classes = className || ''
  classes += fullwidth ? ' fullwidth ' : ''
  classes += placeholder ? ' with-placeholder ' : ''
  const compClasses = enhanced ? ' ui-select__enhanced ' : ''
  const menuRef = useRef(null)

  return (
    <div
      className={`ui-select__container ${classes.trim()}`.trim()}
      style={selectStyle}
      ref={containerRef}
    >
      {!native && <div className="ui-select__menu_anchor" ref={menuRef} />}
      <UISelect
        enhanced={!native ? { hoistToBody: false, fixed: true } : false}
        className={`ui-select ${compClasses.trim()}`.trim()}
        placeholder={placeholder}
        {...rest}
        // @ts-ignore
        menuRef={!native ? menuRef : undefined}
      />
    </div>
  )
}

Select.propTypes = {
  label: string,
  options: oneOfType([
    arrayOf(string),
    arrayOf(
      shape({
        label: string,
        options: arrayOf(
          shape({ label: string, value: string, disabled: bool }),
        ),
      }),
    ),
  ]),
  value: string,
  outlined: bool,
  invalid: bool,
  disabled: bool,
  required: bool,
  enhanced: bool,
  native: bool,
  helpText: oneOfType([node, shape({})]),
  placeholder: string,
  icon: oneOfType([shape({}), string]),
  color: string,
  className: string,
  style: shape({}),
  fullwidth: false,
  rootProps: shape({}),
} as any

Select.defaultProps = {
  label: '',
  options: [],
  value: '',
  outlined: false,
  invalid: false,
  disabled: false,
  required: false,
  enhanced: true,
  native: false,
  helpText: undefined,
  placeholder: '',
  icon: '',
  color: '',
  className: '',
  style: {},
  fullwidth: false,
  rootProps: {},
}

export default Select
